import { CONFIG } from '@utils/constants';
import feedback from '@components/ui/general/feedback';
import useCriticalErrorStore from '@store/useCriticalErrorStore';

/**
 * Unit Validator - FAIL LOUDLY
 * Critical errors are shown to user via feedback system
 * Non-critical issues are logged as warnings
 */

// CRITICAL properties - missing these means unit cannot be rendered correctly
// These trigger ERROR level messages and feedback to user
const CRITICAL_PROPERTIES = ['x', 'y'];

// Properties that should exist on the unit object itself
const UNIT_PROPERTIES = [
  'id',
  'name',
  'x',
  'y',
  'hp',
  'ap',
  'movement',
  'has_acted',
  'is_alive',
  'player_id',
  'unit_template_id',
  'status',
  'teamClass',
  'type'
];

// Properties that should exist in unit.unit_template
const TEMPLATE_PROPERTIES = [
  'max_hp',
  'max_ap',
  'max_movement',
  'armor',
  'will',
  'vision',
  'strength',
  'agility'
];

/**
 * Validates units array - FAIL LOUDLY for critical errors
 * Critical errors (missing x, y, unit_template) show feedback to user
 * Non-critical issues are logged as warnings
 *
 * @param {Array} units - Array of units to validate
 * @param {string} source - Description of where units came from
 * @returns {Object} { valid: boolean, criticalErrors: Array, warnings: Array }
 */
export function validateUnits(units, source = 'unknown') {
  const result = { valid: true, criticalErrors: [], warnings: [] };

  // Check if we have a valid array
  if (!Array.isArray(units)) {
    const msg = `[Unit Validator] Expected array from ${source}, got: ${typeof units}`;
    console.error(msg);
    feedback.errorOnce(`Invalid unit data from ${source}`);
    return { valid: false, criticalErrors: [msg], warnings: [] };
  }

  // Check each unit for missing properties
  units.forEach(unit => {
    if (!unit) {
      const msg = `[Unit Validator] Null unit in ${source}`;
      console.error(msg);
      result.criticalErrors.push(msg);
      result.valid = false;
      return;
    }

    const criticalMissing = [];
    const nonCriticalMissing = [];

    // Check properties that should be on the unit itself
    UNIT_PROPERTIES.forEach(prop => {
      if (unit[prop] === undefined || unit[prop] === null) {
        if (CRITICAL_PROPERTIES.includes(prop)) {
          criticalMissing.push(prop);
        } else {
          nonCriticalMissing.push(prop);
        }
      }
    });

    // Check template - unit_template is CRITICAL
    if (!unit.unit_template) {
      criticalMissing.push('unit_template');
    } else {
      TEMPLATE_PROPERTIES.forEach(prop => {
        if (unit.unit_template[prop] === undefined || unit.unit_template[prop] === null) {
          nonCriticalMissing.push(`unit_template.${prop}`);
        }
      });
    }

    // Handle CRITICAL errors - FAIL LOUDLY
    if (criticalMissing.length > 0) {
      const unitId = unit.id || 'unknown';
      const errorMsg = `Unit #${unitId} has invalid data: missing ${criticalMissing.join(', ')}`;

      // Log as ERROR
      console.error(`[Unit Validator] CRITICAL ERROR in ${source}:`, errorMsg);

      // Show feedback to user (only once per unique message)
      feedback.errorOnce(`Data Error: Unit #${unitId} has invalid data`);

      // Trigger critical error - puts system in standby mode
      // User-friendly medieval themed message, technical details kept for debugging
      useCriticalErrorStore.getState().setCriticalError(
        'The realm has become unstable! Refresh to restore order.',
        {
          source: `unitValidator:${source}`,
          details: [{ unitId, missing: criticalMissing, source }]
        }
      );

      result.criticalErrors.push({ unitId, missing: criticalMissing, source });
      result.valid = false;
    }

    // Handle non-critical issues - just warn (only in debug mode)
    if (nonCriticalMissing.length > 0 && CONFIG.DEBUG_MODE) {
      console.warn(`[Unit Validator] Unit #${unit.id || 'unknown'} from ${source} missing:`, nonCriticalMissing);
      result.warnings.push({ unitId: unit.id, missing: nonCriticalMissing });
    }
  });

  return result;
}

/**
 * Validates game snapshots for unit data integrity
 * @param {Object} game - Game object with snapshots
 * @returns {Object} Combined validation results
 */
export function validateGameSnapshots(game) {
  const results = { valid: true, criticalErrors: [], warnings: [] };

  if (!game) return results;

  // Check units in current view
  if (game.dynamic_objects?.units) {
    const r = validateUnits(game.dynamic_objects.units, 'current game');
    mergeResults(results, r);
  }

  // Check snapshots if they exist
  if (game.snapshots) {
    if (game.snapshots.base?.dynamic_objects?.units) {
      const r = validateUnits(game.snapshots.base.dynamic_objects.units, 'base snapshot');
      mergeResults(results, r);
    }
    if (game.snapshots.current?.dynamic_objects?.units) {
      const r = validateUnits(game.snapshots.current.dynamic_objects.units, 'current snapshot');
      mergeResults(results, r);
    }
  }

  return results;
}

/**
 * Helper to merge validation results
 */
function mergeResults(target, source) {
  if (!source.valid) target.valid = false;
  target.criticalErrors.push(...source.criticalErrors);
  target.warnings.push(...source.warnings);
}

export function validateUnitAcrossSnapshots(game, unitId) {
  // Not needed in simplified version
  if (!CONFIG.DEBUG_MODE) return { valid: true, criticalErrors: [], warnings: [] };
  console.log(`[Unit Validator] Unit #${unitId} validation across snapshots not implemented in simple version`);
  return { valid: true, criticalErrors: [], warnings: [] };
}

/**
 * Validates a single unit
 * @param {Object} unit - Unit to validate
 * @returns {Object} Validation result
 */
export function validateUnit(unit) {
  if (!unit) {
    const msg = '[Unit Validator] validateUnit called with null unit';
    console.error(msg);
    feedback.errorOnce('Data Error: Attempted to validate null unit');
    return { valid: false, criticalErrors: [msg], warnings: [] };
  }

  return validateUnits([unit], 'single unit check');
}